import { useState, useEffect, useRef } from "react";
import { C, S, G, uid, today, fmtDate, isPast, calcGrade } from "../../utils/theme";
import { Modal, Inp, Txtarea, Badge, Empty, ProgressBar, PageHdr, GlassCard, LevelBadge, FeesBadge } from "../../components/UI";
import { academicAPI, assessmentAPI, commAPI, usersAPI } from "../../utils/api";

// ── Course Access Logic ────────────────────────────────────────────────────
// Backend sends courses of the student's program in order, with locked / passed flags
function getCourseAccess(courses) {
  const level = (key) => {
    const list = courses.filter(c => c.level === key);
    return { courses: list, unlocked: list.some(c => !c.locked), complete: list.length > 0 && list.every(c => c.passed) };
  };
  return { general: level("general"), core: level("core"), specialist: level("specialist") };
}

// ── DASHBOARD ──────────────────────────────────────────────────────────────
export function StudentDashboard({ user, setPage }) {
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [c, a, s, ann] = await Promise.all([
          academicAPI.getCourses(),
          assessmentAPI.getAssignments(),
          assessmentAPI.mySubmissions(),
          commAPI.getAnnouncements(),
        ]);
        setCourses(c.data||[]);
        setAssignments(a.data||[]);
        setSubmissions(s.data||[]);
        setAnnouncements((ann.data||[]).slice(0,3));
      } catch(e) { console.error(e); }
    };
    load();
  }, []);

  const myCourses = courses;
  const pendingAssign = assignments.filter(a => !a.locked && !submissions.find(s => s.assignment_id === a.id) && !isPast(a.due_date));
  const passedExams = myCourses.filter(c => c.passed).length;
  const totalExams = myCourses.length;
  const progress = totalExams > 0 ? Math.round((passedExams / totalExams) * 100) : 0;

  const access = user.program_id ? getCourseAccess(courses) : null;

  return (
    <div>
      {/* Hero Profile Card */}
      <div style={{ background:G.hero, borderRadius:20, padding:"28px 32px", marginBottom:28, display:"flex", gap:28, alignItems:"center", border:"1px solid rgba(255,255,255,0.08)", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-30, right:-30, width:200, height:200, borderRadius:"50%", background:"rgba(59,130,246,0.08)", pointerEvents:"none" }} />
        <div style={{ position:"relative", flexShrink:0 }}>
  <div style={{ width:90, height:90, borderRadius:"50%", background:G.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:40, overflow:"hidden", border:"3px solid rgba(139,92,246,0.5)", boxShadow:"0 8px 25px rgba(139,92,246,0.3)" }}>
    {user.photo ? <img src={user.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "👤"}
  </div>
  <label style={{ position:"absolute", bottom:0, right:0, width:28, height:28, background:G.blue, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", border:"2px solid #0A1628", fontSize:14 }}>
    📷
    <input type="file" accept="image/*" style={{ display:"none" }} onChange={async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const fd = new FormData();
        fd.append("photo", file);
        const { usersAPI } = await import("../../utils/api");
        await usersAPI.updateStudent(user.id, fd);
        window.location.reload();
      } catch(err) { alert(err.message); }
    }} />
  </label>
</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Welcome back</div>
          <div style={{ fontSize:24, fontWeight:800, color:C.white, marginBottom:4 }}>{user.name}</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:14 }}>
            <Badge type="info">🪪 {user.student_id}</Badge>
            {user.degree_name && <Badge type="purple">{user.degree_icon} {user.short_name}</Badge>}
            {user.program_name && <Badge type="gray">{user.program_name}</Badge>}
            <FeesBadge status={user.fees_status||"unpaid"} />
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ flex:1, maxWidth:300 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:12, color:"rgba(255,255,255,0.6)" }}>
                <span>Academic Progress</span><span>{progress}%</span>
              </div>
              <div style={{ height:8, background:"rgba(255,255,255,0.15)", borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${progress}%`, background:"linear-gradient(90deg,#3B82F6,#8B5CF6)", borderRadius:4, transition:"width 0.6s ease" }} />
              </div>
            </div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)" }}>{passedExams}/{totalExams} exams passed</div>
          </div>
        </div>
        {user.fees_status==="unpaid" && (
          <div style={{ background:"rgba(244,63,94,0.15)", border:"1px solid rgba(244,63,94,0.3)", borderRadius:12, padding:"14px 18px", textAlign:"center" }}>
            <div style={{ fontSize:24 }}>❌</div>
            <div style={{ fontSize:12, fontWeight:700, color:"#F43F5E", marginTop:4 }}>Fees Overdue</div>
            <div style={{ fontSize:11, color:"#64748B", marginTop:2 }}>Contact admin</div>
          </div>
        )}
        {user.fees_status==="partial" && (
          <div style={{ background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:12, padding:"14px 18px", textAlign:"center" }}>
            <div style={{ fontSize:24 }}>⚠️</div>
            <div style={{ fontSize:12, fontWeight:700, color:"#F59E0B", marginTop:4 }}>Partial Paid</div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={S.statsGrid}>
        {[
          { icon:"📚", label:"My Courses", value:myCourses.length, color:"#3B82F6" },
          { icon:"📝", label:"Pending Tasks", value:pendingAssign.length, color:pendingAssign.length>0?"#F59E0B":"#10B981" },
          { icon:"✅", label:"Submitted", value:submissions.length, color:"#10B981" },
          { icon:"🎯", label:"Exams Passed", value:passedExams, color:"#8B5CF6" },
        ].map((s,i)=>(
          <div key={i} style={{ ...S.card, padding:"20px 22px", borderTop:`3px solid ${s.color}` }}>
            <div style={{ fontSize:24, marginBottom:8 }}>{s.icon}</div>
            <div style={{ fontSize:26, fontWeight:900, color:C.white }}>{s.value}</div>
            <div style={{ fontSize:12, color:"#64748B", marginTop:5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Course Progression */}
      {access && (
        <div style={{ ...S.card, marginBottom:24, padding:"20px 24px" }}>
          <div style={{ fontWeight:700, color:C.white, marginBottom:16, fontSize:15 }}>🎓 Course Progression</div>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            {[
              { label:"General", data:access.general, color:"#3B82F6", icon:"🔵" },
              { label:"Core", data:access.core, color:"#8B5CF6", icon:"🟣" },
              { label:"Specialist", data:access.specialist, color:"#F59E0B", icon:"🟡" },
            ].map(lv=>(
              <div key={lv.label} style={{ flex:1, minWidth:180, padding:"14px 16px", background:lv.data.unlocked?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.2)", borderRadius:12, border:`1px solid ${lv.data.unlocked?lv.color+"44":"rgba(255,255,255,0.04)"}`, opacity:lv.data.unlocked?1:0.5 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:20 }}>{lv.data.unlocked?lv.icon:"🔒"}</span>
                  <span style={{ fontWeight:700, color:lv.data.unlocked?C.white:"#475569", fontSize:14 }}>{lv.label}</span>
                  {lv.data.complete && <Badge type="success">✓ Done</Badge>}
                  {!lv.data.unlocked && <Badge type="locked">Locked</Badge>}
                </div>
                <div style={{ fontSize:12, color:"#64748B" }}>{lv.data.courses.length} courses</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <div style={S.card}>
          <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}><span style={{ fontWeight:700, color:C.white }}>📢 Announcements</span></div>
          <div style={{ padding:"8px 0" }}>
            {announcements.length===0 ? <div style={{ padding:"20px", color:"#475569", fontSize:13, textAlign:"center" }}>No announcements</div>
              : announcements.map(a=>(
                <div key={a.id} style={{ padding:"11px 20px", borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                  <div style={{ fontWeight:600, fontSize:13, color:C.white }}>{a.title}</div>
                  <div style={{ fontSize:12, color:"#64748B", marginTop:3, lineHeight:1.5 }}>{a.message.substring(0,80)}...</div>
                  <div style={{ fontSize:11, color:"#334155", marginTop:4 }}>{new Date(a.created_at).toLocaleDateString("en-PK")}</div>
                </div>
              ))
            }
          </div>
        </div>
        <div style={S.card}>
          <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}><span style={{ fontWeight:700, color:C.white }}>⏰ Upcoming Deadlines</span></div>
          <div style={{ padding:"8px 0" }}>
            {pendingAssign.length===0 ? <div style={{ padding:"20px", color:"#10B981", fontSize:13, textAlign:"center" }}>✅ All clear!</div>
              : pendingAssign.slice(0,3).map(a=>{
                const days = Math.ceil((new Date(a.due_date)-new Date())/86400000);
                return (
                  <div key={a.id} style={{ padding:"11px 20px", borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                    <div style={{ fontWeight:600, fontSize:13, color:C.white }}>{a.title}</div>
                    <div style={{ fontSize:12, color:days<=3?"#F43F5E":"#F59E0B", marginTop:3 }}>⏰ {fmtDate(a.due_date)} ({days}d left)</div>
                  </div>
                );
              })
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MY COURSES ─────────────────────────────────────────────────────────────
export function StudentCourses({ user }) {
  const [courses, setCourses] = useState([]);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const c = await academicAPI.getCourses();
        setCourses(c.data||[]);
      } catch(e) { console.error(e); }
    };
    load();
  }, []);

  const access = user.program_id ? getCourseAccess(courses) : null;

  if (!access) return <Empty icon="📚" text="No program assigned" sub="Contact admin to assign a program" />;

  if (sel) {
    return (
      <div>
        <button onClick={()=>setSel(null)} style={{ ...S.btn("ghost","sm"), marginBottom:20, color:"#3B82F6" }}>← Back</button>
        <div style={S.card}>
          <div style={{ padding:"22px 26px 16px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
              <div>
                <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8 }}>
                  <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:C.white }}>{sel.name}</h2>
                  <LevelBadge level={sel.level} />
                  <Badge type="info">{sel.code}</Badge>
                </div>
                <div style={{ fontSize:13, color:"#64748B" }}>{sel.short_name} → {sel.program_name} • {sel.credits} credits</div>
              </div>
            </div>
          </div>
          <div style={{ padding:"22px 26px" }}>
            <h3 style={{ fontSize:15, fontWeight:700, color:C.white, marginBottom:12 }}>📋 Course Outline</h3>
            <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:12, padding:18, whiteSpace:"pre-line", fontSize:14, color:"#94A3B8", lineHeight:2, border:"1px solid rgba(255,255,255,0.06)" }}>
              {sel.outline || "No outline available."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const levelConfig = [
    { key:"general",    label:"General Courses",    icon:"🔵", color:"#3B82F6", desc:"Foundation courses — pass each exam to unlock the next course" },
    { key:"core",       label:"Core Courses",       icon:"🟣", color:"#8B5CF6", desc:"Unlocks after all General courses are passed" },
    { key:"specialist", label:"Specialist Courses", icon:"🟡", color:"#F59E0B", desc:"Unlocks after all Core courses are passed" },
  ];

  return (
    <div>
      <PageHdr title="My Courses" sub={`${user.degree_name} — ${user.program_name}`} />
      {levelConfig.map(lv=>{
        const lvData = access[lv.key];
        return (
          <div key={lv.key} style={{ marginBottom:32 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
              <span style={{ fontSize:22 }}>{lvData.unlocked?lv.icon:"🔒"}</span>
              <div>
                <div style={{ fontWeight:800, fontSize:17, color:lvData.unlocked?C.white:"#334155" }}>{lv.label}</div>
                <div style={{ fontSize:12, color:"#475569" }}>{lv.desc}</div>
              </div>
              {lvData.complete && <Badge type="success">✅ Complete</Badge>}
              {!lvData.unlocked && <Badge type="locked">🔒 Locked</Badge>}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))", gap:14 }}>
              {lvData.courses.map((c,i)=>(
                <GlassCard key={c.id} onClick={()=>{ if (c.locked) alert("🔒 Pass the previous course's exam to unlock this course."); else setSel(c); }}
                  style={{ borderLeft:`3px solid ${c.passed?"#10B981":c.locked?"#334155":lv.color}`, cursor:c.locked?"not-allowed":"pointer", opacity:c.locked?0.5:1 }}>
                  <div style={{ padding:18 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                      <div style={{ fontWeight:700, fontSize:14, color:C.white, flex:1, paddingRight:8 }}>{c.locked?"🔒 ":""}{i+1}. {c.name}</div>
                      {c.passed ? <Badge type="success">✅ Passed</Badge> : c.locked ? <Badge type="locked">🔒 Locked</Badge> : <Badge type="info">📖 In Progress</Badge>}
                    </div>
                    <div style={{ fontSize:12, color:"#64748B", marginBottom:8 }}>{c.code} • {c.credits} credits</div>
                    <div style={{ fontSize:12, color:"#94A3B8", marginBottom:12, lineHeight:1.5 }}>{c.description}</div>
                    <div style={{ fontSize:12, color:c.locked?"#475569":lv.color, fontWeight:600 }}>{c.locked?"Locked":"View Course →"}</div>
                  </div>
                </GlassCard>
              ))}
              {lvData.courses.length===0 && <div style={{ color:"#475569", fontSize:13, padding:16 }}>No courses in this level yet.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── LECTURES ────────────────────────────────────────────────────────────────
export function StudentLectures({ user }) {
  const [lectures, setLectures] = useState([]);
  const [sel, setSel] = useState(null);

  const load = async () => {
    try { const l = await academicAPI.getLectures(); setLectures(l.data||[]); } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const open = (l) => {
    if (l.locked) {
      alert(l.course_locked ? "🔒 Pass the previous course's exam to unlock this course." : "🔒 Open the previous lecture first.");
      return;
    }
    setSel(l);
    // Opening a lecture marks it viewed → next lecture unlocks
    if (!l.viewed) academicAPI.markLectureViewed(l.id).then(load).catch(console.error);
  };

  const embedUrl = (url) => {
    if (!url) return "";
    const m = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : url;
  };

  if (sel) {
    return (
      <div>
        <button onClick={()=>setSel(null)} style={{ ...S.btn("ghost","sm"), marginBottom:20, color:"#3B82F6" }}>← Back</button>
        <div style={S.card}>
          <div style={{ padding:"20px 26px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
            <h2 style={{ margin:"0 0 6px", fontSize:18, fontWeight:800, color:C.white }}>{sel.title}</h2>
            <div style={{ display:"flex", gap:8 }}>
              {sel.module_name && <Badge type="gray">📦 {sel.module_name}</Badge>}
              {sel.course_level && <LevelBadge level={sel.course_level} />}
              <Badge type={sel.type==="youtube"?"danger":"success"}>{sel.type==="youtube"?"YouTube":"Upload"}</Badge>
            </div>
            {sel.description && <p style={{ margin:"12px 0 0", fontSize:14, color:"#64748B" }}>{sel.description}</p>}
          </div>
          <div style={{ padding:24 }}>
            {sel.type==="youtube"
              ? <div style={{ borderRadius:14, overflow:"hidden", background:"#000", aspectRatio:"16/9" }}>
                  <iframe src={embedUrl(sel.url)} width="100%" height="100%" style={{ border:"none", display:"block" }} allowFullScreen title={sel.title} />
                </div>
              : <div>
                  {sel.video_url && (
                    <div style={{ borderRadius:14, overflow:"hidden", background:"#000", aspectRatio:"16/9", marginBottom:16 }}>
                      <video src={sel.video_url} controls style={{ width:"100%", height:"100%", display:"block" }} />
                    </div>
                  )}
                  {sel.pdf_url && (
                    <div style={{ padding:"18px 22px", background:"rgba(59,130,246,0.08)", borderRadius:12, border:"1px solid rgba(59,130,246,0.2)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <span style={{ fontSize:28 }}>📄</span>
                        <div>
                          <div style={{ fontWeight:700, color:C.white, fontSize:14 }}>{sel.pdf_name||"Lecture PDF"}</div>
                          <div style={{ fontSize:12, color:"#64748B" }}>Lecture notes / slides</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:10 }}>
  <a href={sel.pdf_url} target="_blank" rel="noopener noreferrer"
    style={{ ...S.btn("primary","md"), textDecoration:"none" }}>
    👁️ View PDF
  </a>
  <a href={sel.pdf_url} download={sel.pdf_name||"lecture.pdf"}
    style={{ ...S.btn("glass","md"), textDecoration:"none" }}>
    📥 Download
  </a>
</div>
                    </div>
                  )}
                  {!sel.video_url && !sel.pdf_url && (
                    <div style={{ background:"rgba(255,255,255,0.03)", borderRadius:14, padding:48, textAlign:"center", border:"2px dashed rgba(255,255,255,0.08)" }}>
                      <div style={{ fontSize:52, marginBottom:12 }}>🎬</div>
                      <div style={{ fontSize:16, fontWeight:600, color:C.white }}>No media uploaded yet</div>
                    </div>
                  )}
                </div>
            }
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHdr title="My Lectures" sub={`${lectures.filter(l=>!l.locked).length}/${lectures.length} lectures unlocked`} />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))", gap:16 }}>
        {lectures.map(l=>{
          const thumbId = l.url?.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/)?.[1];
          return (
            <GlassCard key={l.id} onClick={()=>open(l)} style={{ opacity:l.locked?0.5:1, cursor:l.locked?"not-allowed":"pointer" }}>
              <div style={{ aspectRatio:"16/9", background:l.locked?"rgba(0,0,0,0.4)":thumbId?`url(https://img.youtube.com/vi/${thumbId}/mqdefault.jpg) center/cover`:G.blue, display:"flex", alignItems:"center", justifyContent:"center", position:"relative" }}>
                <div style={{ width:50, height:50, background:"rgba(255,255,255,0.9)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{l.locked?"🔒":"▶"}</div>
                <div style={{ position:"absolute", top:8, right:8 }}>
                  {l.locked ? <Badge type="locked">Locked</Badge> : l.viewed ? <Badge type="success">✓ Viewed</Badge> : <Badge type={l.type==="youtube"?"danger":"info"}>{l.type==="youtube"?"YT":"Upload"}</Badge>}
                </div>
                {l.course_level && <div style={{ position:"absolute", top:8, left:8 }}><LevelBadge level={l.course_level} /></div>}
              </div>
              <div style={{ padding:14 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.white, marginBottom:4 }}>{l.locked?"🔒 ":""}{l.title}</div>
                <div style={{ fontSize:12, color:"#475569" }}>📚 {l.course_name}{l.module_name && l.module_name!==l.course_name ? ` • ${l.module_name}` : ""}</div>
              </div>
            </GlassCard>
          );
        })}
        {lectures.length===0 && <Empty icon="🎬" text="No lectures yet" />}
      </div>
    </div>
  );
}

// ── ASSIGNMENTS ─────────────────────────────────────────────────────────────
export function StudentAssignments({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [submitModal, setSubmitModal] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [a, s] = await Promise.all([assessmentAPI.getAssignments(), assessmentAPI.mySubmissions()]);
      setAssignments(a.data||[]); setSubmissions(s.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!pdfFile) { alert("Select a PDF file."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("pdf", pdfFile);
      await assessmentAPI.submitAssignment(submitModal.id, fd);
      setSubmitModal(null); setPdfFile(null); load();
    } catch(e) { alert(e.message); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <PageHdr title="My Assignments" sub={`${assignments.length} total`} />
      {assignments.map(a=>{
        const sub = submissions.find(s => s.assignment_id===a.id);
        const past = a.due_date && new Date(a.due_date) < new Date();
        const days = !past ? Math.ceil((new Date(a.due_date)-new Date())/86400000) : 0;
        const borderColor = a.locked?"#334155":sub?"#10B981":past?"#F43F5E":"#F59E0B";
        return (
          <div key={a.id} style={{ ...S.card, marginBottom:14, borderLeft:`3px solid ${borderColor}`, opacity:a.locked?0.5:1 }}>
            <div style={{ padding:"18px 22px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", marginBottom:8 }}>
                    <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:C.white }}>{a.locked?"🔒 ":""}{a.title}</h3>
                    {a.locked ? <Badge type="locked">🔒 Locked</Badge> : (
                      <Badge type={sub?(sub.status==="graded"?"success":"info"):past?"danger":"warning"}>
                        {sub?sub.status.replace("-"," "):past?"overdue":"pending"}
                      </Badge>
                    )}
                    {a.course_level && <LevelBadge level={a.course_level} />}
                  </div>
                  <p style={{ margin:"0 0 10px", fontSize:13, color:"#64748B" }}>{a.description}</p>
                  <div style={{ display:"flex", gap:14, fontSize:12, color:"#475569", flexWrap:"wrap" }}>
                    {a.course_name && <span>📚 {a.course_name}</span>}
                    <span>📅 {a.due_date?new Date(a.due_date).toLocaleDateString("en-PK"):"—"}</span>
                    {!a.locked && !past && !sub && <span style={{ color:days<=3?"#F43F5E":"#F59E0B" }}>⏰ {days}d left</span>}
                    {a.locked && <span>Unlocks when you reach this course</span>}
                  </div>
                  {a.pdf_url && (
                    <a href={a.pdf_url} download={a.pdf_name||"assignment.pdf"} target="_blank" rel="noopener noreferrer"
                      style={{ ...S.btn("glass","sm"), marginTop:10, textDecoration:"none" }}>
                      📄 Download Question Paper
                    </a>
                  )}
                  {sub?.marks!=null && (
                    <div style={{ marginTop:12, display:"inline-flex", alignItems:"center", gap:10, padding:"10px 16px", background:"rgba(16,185,129,0.1)", borderRadius:10 }}>
                      <span style={{ fontWeight:800, color:"#10B981", fontSize:20 }}>{sub.marks}%</span>
                      {sub.feedback && <span style={{ fontSize:12, color:"#64748B" }}>{sub.feedback}</span>}
                    </div>
                  )}
                  {sub?.status==="auto-submitted" && <div style={{ marginTop:8, fontSize:12, color:"#F59E0B" }}>⚡ Auto-submitted on due date.</div>}
                </div>
                <div style={{ flexShrink:0 }}>
                  {!a.locked && !sub && !past && <button style={S.btn("primary")} onClick={()=>{ setSubmitModal(a); setPdfFile(null); }}>📤 Submit</button>}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {assignments.length===0 && <Empty icon="📝" text="No assignments yet" />}
      <Modal open={!!submitModal} onClose={()=>setSubmitModal(null)} title={`Submit: ${submitModal?.title}`}
        footer={<><button style={S.btn("ghost")} onClick={()=>setSubmitModal(null)}>Cancel</button><button style={S.btn("primary")} onClick={submit} disabled={uploading}>{uploading?"Uploading...":"📤 Submit"}</button></>}>
        <div style={S.alert("info")}>Upload your completed assignment as PDF.</div>
        <div style={S.formGroup}>
          <label style={S.label}>Select PDF *</label>
          <input type="file" accept=".pdf" onChange={e=>setPdfFile(e.target.files[0])} style={{ color:"#94A3B8", fontSize:13, display:"block", padding:"8px 0" }} />
          {pdfFile && <div style={{ fontSize:13, color:"#10B981", marginTop:8 }}>✅ {pdfFile.name}</div>}
        </div>
      </Modal>
    </div>
  );
}

// ── EXAMS ────────────────────────────────────────────────────────────────────
export function StudentExams({ user }) {
  const [exams, setExams] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [activeExam, setActiveExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const [e, r] = await Promise.all([assessmentAPI.getExams(), assessmentAPI.myResults()]);
      setExams(e.data||[]); setMyResults(r.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!activeExam || timeLeft===null) return;
    timerRef.current = setInterval(()=>{
      setTimeLeft(t=>{ if(t<=1){ clearInterval(timerRef.current); doSubmit(); return 0; } return t-1; });
    }, 1000);
    return ()=>clearInterval(timerRef.current);
  }, [activeExam]);

  // Backend already sends only this student's program exams, in course order
  const myExams = exams;
  const lockMsg = () => "Pass the previous course's exam first";

  const start = (exam) => {
    if (myResults.find(r=>r.exam_id===exam.id)) { alert("Already taken!"); return; }
    if (exam.locked) { alert(`🔒 ${lockMsg(exam)}.`); return; }
    setActiveExam(exam); setAnswers({}); setTimeLeft(exam.duration*60); setResult(null);
  };

  const doSubmit = async () => {
    clearInterval(timerRef.current);
    const exam = activeExam;
    try {
      const res = await assessmentAPI.submitExam(exam.id, answers);
      setResult({ res:res.data, exam }); setActiveExam(null); load();
    } catch(e) { alert(e.message); }
  };

  const fmt = s=>`${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  if (result) return (
    <div style={{ maxWidth:520, margin:"0 auto", textAlign:"center", paddingTop:20 }}>
      <div style={{ fontSize:72, marginBottom:12 }}>🎓</div>
      <h2 style={{ fontSize:26, fontWeight:800, color:"#10B981", marginBottom:6 }}>Exam Submitted!</h2>
      <div style={{ ...S.card, padding:32, marginBottom:22 }}>
        <div style={{ fontSize:72, fontWeight:900, color:"#10B981", lineHeight:1 }}>{result.res.final_score}%</div>
        <div style={{ margin:"10px 0 14px" }}><Badge type="success">✓ PASSED</Badge></div>
        {result.res.score<60 && <div style={S.alert("warning")}>⚡ Raw: {result.res.score}% → Auto-boosted to 60%</div>}
      </div>
      <button style={S.btn("primary")} onClick={()=>setResult(null)}>← Back</button>
    </div>
  );

  if (activeExam) return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
        <h2 style={S.pageTitle}>{activeExam.title}</h2>
        <div style={{ display:"flex", gap:12 }}>
          <div style={{ padding:"9px 18px", background:timeLeft<60?"rgba(244,63,94,0.15)":"rgba(59,130,246,0.15)", color:timeLeft<60?"#F43F5E":"#3B82F6", borderRadius:10, fontWeight:800, fontSize:20, fontFamily:"monospace" }}>⏱ {fmt(timeLeft)}</div>
          <button style={S.btn("primary")} onClick={doSubmit}>Submit</button>
        </div>
      </div>
      {(activeExam.questions||[]).map((q,i)=>(
        <div key={q.id||i} style={{ ...S.card, marginBottom:14 }}>
          <div style={{ padding:22 }}>
            <div style={{ fontWeight:700, fontSize:15, color:C.white, marginBottom:16 }}>Q{i+1}. {q.question}</div>
            {(q.options||(q.options?JSON.parse(q.options):[])).map((opt,j)=>(
              <label key={j} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 16px", borderRadius:10, border:`2px solid ${answers[q.id||i]===j?"#3B82F6":"rgba(255,255,255,0.06)"}`, background:answers[q.id||i]===j?"rgba(59,130,246,0.1)":"transparent", cursor:"pointer", marginBottom:8, color:answers[q.id||i]===j?"#3B82F6":"#94A3B8", fontWeight:answers[q.id||i]===j?600:400 }}>
                <input type="radio" name={q.id||i} checked={answers[q.id||i]===j} onChange={()=>setAnswers({...answers,[q.id||i]:j})} style={{ display:"none" }} />
                <span style={{ width:20, height:20, borderRadius:"50%", border:`2px solid ${answers[q.id||i]===j?"#3B82F6":"rgba(255,255,255,0.15)"}`, background:answers[q.id||i]===j?"#3B82F6":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {answers[q.id||i]===j && <span style={{ width:7, height:7, background:C.white, borderRadius:"50%", display:"block" }} />}
                </span>
                {opt}
              </label>
            ))}
          </div>
        </div>
      ))}
      <div style={{ textAlign:"center", marginTop:20 }}>
        <button style={{ ...S.btn("primary","lg"), minWidth:180 }} onClick={doSubmit}>📋 Submit Exam</button>
      </div>
    </div>
  );

  return (
    <div>
      <PageHdr title="My Exams" sub="Min 60% guaranteed" />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 }}>
        {myExams.map(e=>{
          const res = myResults.find(r=>r.exam_id===e.id);
          return (
            <GlassCard key={e.id} style={{ opacity:!res&&e.locked?0.5:1 }}>
              <div style={{ padding:22 }}>
                <div style={{ width:46, height:46, background:res?"rgba(16,185,129,0.15)":"rgba(59,130,246,0.15)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, marginBottom:14 }}>{res?"✅":e.locked?"🔒":"📋"}</div>
                <h3 style={{ margin:"0 0 5px", fontSize:14, fontWeight:700, color:C.white }}>{e.title}</h3>
                {e.course_level && <div style={{ marginBottom:8 }}><LevelBadge level={e.course_level} /></div>}
                <p style={{ margin:"0 0 14px", fontSize:12, color:"#64748B" }}>{e.course_name}</p>
                <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                  <Badge type="info">{e.question_count ?? (e.questions||[]).length}Q</Badge>
                  <Badge type="gray">⏱ {e.duration}min</Badge>
                </div>
                {res ? (
                  <div style={{ padding:"12px 0", textAlign:"center" }}>
                    <div style={{ fontSize:32, fontWeight:900, color:"#10B981" }}>{res.final_score}%</div>
                    <Badge type="success">PASSED ✓</Badge>
                  </div>
                ) : e.locked ? (
                  <div style={{ padding:"10px 0", textAlign:"center", fontSize:12, color:"#64748B" }}>🔒 {lockMsg(e)}</div>
                ) : <button style={{ ...S.btn("primary"), width:"100%", justifyContent:"center" }} onClick={()=>start(e)}>Start Exam →</button>}
              </div>
            </GlassCard>
          );
        })}
        {myExams.length===0 && <Empty icon="📋" text="No exams yet" />}
      </div>
    </div>
  );
}

// ── GRADES ────────────────────────────────────────────────────────────────
export function StudentGrades({ user }) {
  const [submissions, setSubmissions] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r, a] = await Promise.all([assessmentAPI.mySubmissions(), assessmentAPI.myResults(), assessmentAPI.getAssignments()]);
        setSubmissions(s.data||[]); setExamResults(r.data||[]); setAssignments(a.data||[]);
      } catch(e) { console.error(e); }
    };
    load();
  }, []);

  const gradedSubs = submissions.filter(s=>s.marks!=null);
  const avgA = gradedSubs.length>0 ? Math.round(gradedSubs.reduce((a,s)=>a+s.marks,0)/gradedSubs.length) : null;
  const avgE = examResults.length>0 ? Math.round(examResults.reduce((a,r)=>a+r.final_score,0)/examResults.length) : null;
  const overall = avgA!=null&&avgE!=null ? Math.round((avgA+avgE)/2) : avgA??avgE;
  const grade = calcGrade(overall);

  return (
    <div>
      <PageHdr title="My Grades" />
      {overall!=null && (
        <div style={{ background:G.blue, borderRadius:16, padding:"24px 30px", marginBottom:24, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:14, color:"rgba(255,255,255,0.7)", marginBottom:4 }}>Overall Performance</div>
            <div style={{ fontSize:36, fontWeight:900, color:C.white }}>{overall}%</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)", marginTop:4 }}>Assignments: {avgA??"—"}% | Exams: {avgE??"—"}%</div>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:64, fontWeight:900, color:C.white, lineHeight:1 }}>{grade}</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)" }}>Final Grade</div>
          </div>
        </div>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <div style={S.card}>
          <div style={{ padding:"13px 18px", borderBottom:"1px solid rgba(255,255,255,0.06)", fontWeight:700, color:C.white }}>📝 Assignments</div>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Assignment</th><th style={S.th}>Marks</th><th style={S.th}>Status</th></tr></thead>
            <tbody>
              {assignments.map(a=>{
                const sub = submissions.find(s=>s.assignment_id===a.id);
                return <tr key={a.id}><td style={S.td}>{a.title}</td><td style={S.td}>{sub?.marks!=null?<strong style={{ color:"#10B981" }}>{sub.marks}%</strong>:<Badge type="gray">—</Badge>}</td><td style={S.td}><Badge type={sub?(sub.status==="graded"?"success":"warning"):"gray"}>{sub?sub.status:"—"}</Badge></td></tr>;
              })}
              {assignments.length===0 && <tr><td colSpan={3} style={{ ...S.td, textAlign:"center", color:"#475569" }}>No assignments yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={S.card}>
          <div style={{ padding:"13px 18px", borderBottom:"1px solid rgba(255,255,255,0.06)", fontWeight:700, color:C.white }}>📋 Exams</div>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Exam</th><th style={S.th}>Score</th><th style={S.th}>Level</th></tr></thead>
            <tbody>
              {examResults.length===0 ? <tr><td colSpan={3} style={{ ...S.td, textAlign:"center", color:"#475569" }}>No exams yet</td></tr>
                : examResults.map(r=>(
                  <tr key={r.id}>
                    <td style={S.td}>{r.exam_title}</td>
                    <td style={S.td}><strong style={{ color:"#10B981" }}>{r.final_score}%</strong>{r.score<60&&<span style={{ fontSize:9, color:"#F59E0B" }}> ⚡</span>}</td>
                    <td style={S.td}>{r.course_level&&<LevelBadge level={r.course_level} />}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── ANNOUNCEMENTS ──────────────────────────────────────────────────────────
export function StudentAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    const load = async () => {
      try { const a = await commAPI.getAnnouncements(); setAnnouncements(a.data||[]); } catch(e) { console.error(e); }
    };
    load();
  }, []);

  return (
    <div>
      <PageHdr title="Announcements" />
      {announcements.length===0 ? <Empty icon="📢" text="No announcements" /> : announcements.map(a=>(
        <div key={a.id} style={{ ...S.card, marginBottom:14, borderLeft:"3px solid #3B82F6" }}>
          <div style={{ padding:"18px 22px" }}>
            <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:18 }}>📢</span>
              <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:C.white }}>{a.title}</h3>
            </div>
            <p style={{ margin:"0 0 10px", fontSize:14, color:"#94A3B8", lineHeight:1.7 }}>{a.message}</p>
            <div style={{ fontSize:12, color:"#475569" }}>{new Date(a.created_at).toLocaleDateString("en-PK")}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
// ── CONTROL PANEL (Degree Re-Apply) ─────────────────────────────────────────
export function StudentControlPanel({ user }) {
  const [form, setForm] = useState({ student_code:"", reason:"" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.student_code.trim() || !form.reason.trim()) { alert("Please enter your Student ID and reason."); return; }
    setSending(true);
    try {
      await usersAPI.submitReapply(form);
      setForm({ student_code:"", reason:"" });
      setDone(true);
    } catch(e) { alert(e.message); }
    finally { setSending(false); }
  };

  return (
    <div>
      <PageHdr title="Control Panel" />
      <div style={{ ...S.card, maxWidth:640 }}>
        <div style={{ padding:"16px 22px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontWeight:700, color:C.white, fontSize:15 }}>🔁 Re-Apply Your Degree</div>
        </div>
        <div style={{ padding:22 }}>
          <Inp label="Name" value={user.name||""} disabled />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <Inp label="Degree" value={user.degree_name||"—"} disabled />
            <Inp label="Program" value={user.program_name||"—"} disabled />
          </div>
          <Inp label="Student ID *" placeholder="Enter your Student ID" value={form.student_code} onChange={e=>setForm({...form,student_code:e.target.value})} />
          <Txtarea label="Reason *" rows={4} placeholder="Why do you want to re-apply?" value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} />
          <button style={S.btn("primary")} onClick={submit} disabled={sending}>{sending?"Submitting...":"📤 Submit"}</button>
        </div>
      </div>
      <Modal open={done} onClose={()=>setDone(false)} title="Request Submitted"
        footer={<button style={S.btn("primary")} onClick={()=>setDone(false)}>OK</button>}>
        <div style={{ textAlign:"center", padding:"10px 0" }}>
          <div style={{ fontSize:52, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:15, color:C.white, fontWeight:600 }}>We will get back to you in 48 to 72 hours.</div>
        </div>
      </Modal>
    </div>
  );
}
